import { gql, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import { IQueryFilter } from '../../type'

const gqlQuery = gql`
  query ListVariableLaborCoverage(
    $iStartDate: Date!
    $iEndDate: Date!
    $iParams: JSON!
  ) {
    listLocationMetricValuesV2(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iParams: $iParams
    ) {
      nodes {
        startDate
        endDate
        metricData
      }
    }
  }
`

interface ICoverageNode {
  startDate: string
  endDate: string
  metricData: Record<string, { value: number } | null> | null
}

/**
 * Pre-query for the MWB Ops Scorecard date-range clamp (ENG-5468).
 *
 * variable_labor posts one weekly row on week-ending Fridays (MWB weeks run
 * Saturday to Friday) while net_sales is daily, so any range extending past
 * the last posted labor week divides a partial-week labor numerator by a
 * full-range sales denominator, understating labor % and flipping the sign
 * of labor_variance. This hook finds the last business day in the range with
 * posted variable_labor so the caller can clamp the labor_variance query to
 * it. Only the labor_variance query is clamped - the quarterly bonus metrics
 * are stored solely on the latest past Friday of the quarter (kit-core
 * normalize/bonus), so clamping the whole scorecard range would zero them out.
 *
 * Returns coveredEndDate = null when no day in the range has posted labor
 * (zero coverage) — the caller renders Labor Variance blank in that case.
 */
function useVariableLaborCoveredEndDate(
  startDate: string,
  endDate: string,
  queryFilter: IQueryFilter,
) {
  const { data, loading } = useQuery(gqlQuery, {
    variables: {
      iStartDate: startDate,
      iEndDate: endDate,
      iParams: {
        location_ids: queryFilter?.locationIds,
        location_group_ids: queryFilter?.locationGroupIds,
        intersected_location_group_ids:
          queryFilter?.intersectedLocationGroupIds,
        // why: company-level BUSINESS_DAY granularity is sufficient - MWB R365
        // labor journals post fleet-wide (all 21 locations or none), so no
        // per-location coverage handling is needed (assumption recorded on
        // ENG-5468).
        group_by: ['BUSINESS_DAY'],
        metrics: ['variable_labor'],
      },
    },
    skip: !startDate || !endDate || !queryFilter,
  })

  return {
    isLoading: loading,
    coveredEndDate: useMemo((): string | null => {
      const nodes: ICoverageNode[] | undefined =
        data?.listLocationMetricValuesV2?.nodes

      if (!nodes) {
        return null
      }

      // why: use > 0 instead of "is not null" - non-Fridays and unposted
      // Fridays carry no value, and UE aggregation may serve "no value" as
      // null, 0, or an absent row. A genuine fleet-week $0 labor cannot occur
      // (getSales.ts stores 0 as null at the source), so > 0 is robust to all
      // three servings.
      const postedDates = nodes
        .filter((node) => (node?.metricData?.variable_labor?.value ?? 0) > 0)
        .map((node) => node.startDate)
        .sort()

      return postedDates.length > 0 ? postedDates[postedDates.length - 1] : null
    }, [data]),
  }
}

export default useVariableLaborCoveredEndDate
